'use client'

import * as React from 'react'
import { ChevronDown, X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Command as CommandPrimitive } from 'cmdk'
import { useQuery } from '@tanstack/react-query'
import { filterAutocompleteFilterAutocompletePostOptions } from '@/generated/@tanstack/react-query.gen'
import { useDebounce } from '@/hooks/use-debounce'
import { z } from 'zod'
import { toast } from '../custom-toast'

const resonseSchema = z.object({
  status: z.string(),
  result: z.array(z.string()),
})

type QueryFilterType = 'owner' | 'gsa' | 'wd' | 'main_crop'

interface Filters {
  type: QueryFilterType
  label: string
  placeHolder: string
}

const filters: Filters[] = [
  {
    type: 'owner',
    label: 'Owner Name',
    placeHolder: 'Search Owner Name...',
  },
  {
    type: 'gsa',
    label: 'GSA',
    placeHolder: 'Search GSA...',
  },
  {
    type: 'wd',
    label: 'Water District',
    placeHolder: 'Search Water District...',
  },
  {
    type: 'main_crop',
    label: 'Main Crop',
    placeHolder: 'Search Main Crop...',
  },
]

interface Props {
  counties: string[]
  queryType: QueryFilterType
  selected: string[]
  isTutorial: boolean
  setSelected: (value: string[]) => void
  disabled?: boolean
  hasAbility?: boolean
}

export function QueryFilterMultipleSelectWithAutoComplete({
  counties,
  queryType,
  selected,
  isTutorial,
  setSelected,
  disabled = false,
  hasAbility = false,
}: Props) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState('')
  const debouncedInputValue = useDebounce(inputValue, 500)
  const filterSchema = filters.find((f) => f.type === queryType)

  const { data = [], isFetching } = useQuery({
    ...filterAutocompleteFilterAutocompletePostOptions({
      body: {
        counties: counties,
        query_input: debouncedInputValue.slice(0, 4),
        query_type: queryType,
        session_key: JSON.parse(localStorage.auth).sessionKey,
      },
    }),
    select: (data) => {
      return resonseSchema.parse(data).result
    },
    enabled: counties.length > 0 && !isTutorial,
  })

  const handleUnselect = (value: string) => {
    const newSelectedArray = selected.filter((s) => s !== value)
    setSelected(newSelectedArray)
  }
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current
      if (input) {
        if (e.key === 'Escape') {
          input.blur()
          // setInputValue("")
          setOpen(false)
        }
      }
    },
    []
  )

  const handleSelect = (value: string) => {
    if (selected.includes(value)) {
      toast.warning({
        title: 'Your select has already been selected.',
      })
      return
    }
    setSelected([...selected, value])
    // setInputValue("")
    inputRef?.current?.blur()
    setOpen(false)
  }

  // Data should != [selected]
  const filteredData = data.filter((item) => !selected.includes(item))

  const isInputValueInFilteredData = filteredData.some((item) =>
    item.toLowerCase().includes(inputValue.toLowerCase())
  )
  const hasFilteredData = filteredData.length > 0 && isInputValueInFilteredData

  return (
    <Command
      onKeyDown={handleKeyDown}
      className="overflow-visible bg-transparent"
      // Because this case when input > 4 -> we need to filter data
      filter={
        data.length > 0
          ? (value, search) => {
              return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
            }
          : undefined
      }
    >
      <div className="text-sm">
        <div className="flex flex-col gap-1">
          <div className="relative">
            <CommandPrimitive.Input
              ref={inputRef}
              value={inputValue}
              disabled={disabled}
              onValueChange={setInputValue}
              onBlur={() => setOpen(false)}
              onFocus={() => setOpen(true)}
              placeholder={filterSchema?.placeHolder}
              className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground rounded-lg w-full
              focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary
              disabled:cursor-not-allowed disabled:opacity-50
              "
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              {isFetching ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              ) : hasFilteredData ? (
                <ChevronDown
                  className="h-4 w-4 opacity-50 cursor-pointer"
                  onClick={() => inputRef.current?.focus()}
                />
              ) : (
                <></>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-1">
            {selected.map((value) => {
              return (
                <Badge className="pl-1 pr-0 py-0" key={value} variant="default">
                  <p className="text-xs line-clamp-2 truncate">{value}</p>
                  <button
                    className="rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleUnselect(value)
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onClick={() => handleUnselect(value)}
                  >
                    <X className="h-3 w-3 text-white hover:text-foreground" />
                  </button>
                </Badge>
              )
            })}
          </div>
        </div>
      </div>

      {open && hasFilteredData ? (
        <div className="relative mt-0">
          <CommandList>
            <div className="absolute top-0 z-10 w-full rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in">
              <CommandGroup className="h-full overflow-auto max-h-[200px]">
                {filteredData.map((value, index) => {
                  return (
                    <CommandItem
                      value={value}
                      key={`${value}-${index}`}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onSelect={(_) => {
                        handleSelect(value)
                      }}
                      className={'cursor-pointer py-2'}
                    >
                      {value}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </div>
          </CommandList>
        </div>
      ) : null}
    </Command>
  )
}
